import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ReelModal = ({ reel, onClose }) => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.play().catch(() => {});
  }, []);

  const goViewAll = () => { onClose(); navigate(`/reels/${reel.id}`); };
  const goProduct = (pid) => { onClose(); navigate(`/reels/${reel.id}/${pid}`); };
  const products = reel.products || [];

  return (
    <motion.div className="rm-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        className="rm-modal"
        initial={{ opacity: 0, scale: 0.93, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 24 }}
        transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
        onClick={e => e.stopPropagation()}
      >
        <button className="rm-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* LEFT: video */}
        <div className="rm-left">
          <div className="rm-video-area">
            <video
              ref={videoRef}
              src={reel.video_url}
              autoPlay
              muted={muted}
              loop
              playsInline
              
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <button
              onClick={() => setMuted(m => !m)}
              style={{
                position: 'absolute', bottom: 16, right: 16,
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(28,26,24,0.75)', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', zIndex: 10, fontSize: 16,
              }}
            >
              {muted ? '🔇' : '🔊'}
            </button>
          </div>
          <div className="rm-reel-strip">
            <p className="rm-strip-handle">{reel.handle}</p>
            <h3 className="rm-strip-title">{reel.title}</h3>
          </div>
        </div>

        {/* RIGHT: products */}
        <div className="rm-right">
          <div className="rm-right-head">
            <div>
              <p className="rm-right-eyebrow">Featured in this Reel</p>
              <h4 className="rm-right-heading">Shop the <em>Look</em></h4>
            </div>
            
          </div>
          <div className="rm-prod-list">
            {products.length === 0 ? (
              <p className="rm-no-products">No products linked to this reel yet.</p>
            ) : (
              products.map((p) => (
                <div key={p.id} className="rm-prod-row" onClick={() => goProduct(p.id)}>
                  <div className="rm-prod-thumb">
                    <img src={p.images?.[0] || p.image_1} alt={p.name} />
                    <div className="rm-prod-thumb-hover"><span>View</span></div>
                  </div>
                  <div className="rm-prod-info">
                    <p className="rm-prod-name">{p.name}</p>
                    <div className="rm-prod-prices">
                      <span className="rm-prod-price">{formatPrice(p.sale_price ?? p.original_price)}</span>
                      {p.sale_price && <span className="rm-prod-orig">{formatPrice(p.original_price)}</span>}
                    </div>
                  </div>
                  <button className="rm-prod-view" onClick={(e) => { e.stopPropagation(); goProduct(p.id); }}>View</button>
                </div>
              ))
            )}
          </div>
          <div className="rm-right-foot">
            <button className="rm-btn-full" onClick={goViewAll}>View All Products in this Reel</button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
/* ═══════════════════════════════════════════
   REEL CARD
═══════════════════════════════════════════ */
const ReelCard = ({ reel, index, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef(null);
  const cardRef  = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const card  = cardRef.current;
    if (!video || !card) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      ref={cardRef}
      className="sbr-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(reel)}
    >
      <div className="sbr-img-wrap">
        <video
          ref={videoRef}
          src={reel.video_url}
          muted
          loop
          playsInline
          style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center top', display:'block' }}
        />
        <div className="sbr-gradient" />
        <div className="sbr-top-row">
          <span className="sbr-handle-badge">{reel.handle}</span>
        </div>
<div className="sbr-info">
  <h3 className="sbr-card-title">{reel.title}</h3>
  <p className="sbr-card-sub">tap to shop this look</p>
  <div className="sbr-btns">
    <span className="sbr-btn-shop">Shop Now →</span>
  </div>
</div>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   PAGE
═══════════════════════════════════════════ */
const ShopByReelPage = () => {
  const [activeReel, setActiveReel] = useState(null);
  const [reels, setReels]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const heroRef  = useRef(null);
  const heroView = useInView(heroRef, { once: true, amount: 0.3 });

  useEffect(() => {
    document.title = 'Shop by Reel | Aamira';
    fetch(`${API}/reels`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setReels(data.reels.filter(r => r.is_active));
        else setError(data.error || 'Failed to load reels');
      })
      .catch(() => setError('Failed to connect to server'))
      .finally(() => setLoading(false));
  }, []);

  /* When a reel card is opened, fetch its linked products */
  const handleOpen = async (reel) => {
    if (reel.products) { setActiveReel(reel); return; }
    try {
      const res  = await fetch(`${API}/reels/${reel.id}/products`);
      const data = await res.json();
      setActiveReel({ ...reel, products: data.success ? data.products : [] });
    } catch {
      setActiveReel({ ...reel, products: [] });
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        /* ─── Page ─── */
        .sbr-page { background:#FAF8F4; min-height:100vh; padding-top:162px; font-family:'Jost',sans-serif; position:relative; overflow-x:hidden; }
        .sbr-page::before { content:''; position:fixed; inset:0; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }

        /* ─── Hero ─── */
        .sbr-hero { position:relative; z-index:1; text-align:center; padding:56px clamp(20px,5vw,80px) 50px; border-bottom:1px solid rgba(176,141,87,0.15); background:linear-gradient(to bottom,#F0EDE6,#FAF8F4); }
        .sbr-hero-eyebrow { font-size:10px; font-weight:500; letter-spacing:0.35em; text-transform:uppercase; color:#B08D57; display:flex; align-items:center; justify-content:center; gap:14px; margin-bottom:20px; }
        .sbr-hero-eyebrow::before,.sbr-hero-eyebrow::after { content:''; width:40px; height:1px; background:linear-gradient(to right,transparent,#B08D57); }
        .sbr-hero-eyebrow::after { background:linear-gradient(to left,transparent,#B08D57); }
        .sbr-hero-title { font-family:'Playfair Display',serif; font-size:clamp(38px,6vw,72px); font-weight:700; color:#1C1A18; line-height:1.0; letter-spacing:-0.02em; margin-bottom:8px; }
        .sbr-hero-title em { font-style:italic; font-weight:400; color:#B08D57; display:block; }
        .sbr-hero-desc { font-family:'Cormorant Garamond',serif; font-size:clamp(15px,1.8vw,19px); font-style:italic; font-weight:300; color:#8A7E72; letter-spacing:0.03em; margin-top:12px; }
        .sbr-hero-tagline { font-size:12px; font-weight:400; letter-spacing:0.16em; text-transform:uppercase; color:#B08D57; margin-top:24px; }

        /* ─── Inner ─── */
        .sbr-inner { position:relative; z-index:1; width:100%; padding:0 0 100px; }

        /* ─── States ─── */
        .sbr-loading { text-align:center; padding:80px; font-size:13px; color:#9A9186; letter-spacing:0.1em; }
        .sbr-error   { text-align:center; padding:40px; color:#C0392B; font-size:13px; }
        .sbr-empty   { text-align:center; padding:80px 20px; }
        .sbr-empty-title { font-family:'Playfair Display',serif; font-size:22px; color:#3a3530; margin-bottom:8px; }

        /* ─── Masonry ─── */
.sbr-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;     /* ← white separation */
  width: 100%;
  background: #FAF8F4;  /* ← this color shows through as the gap */
}

       .sbr-card { 
  break-inside: avoid; margin-bottom: 16px; position: relative; 
  overflow: hidden; background: #1C1A18; cursor: pointer; display: block; 
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  border-radius: 3px;
}
.sbr-card:hover { 
  transform: translateY(-6px) scale(1.01); 
  box-shadow: 0 24px 64px rgba(28,26,24,0.28); 
}
.sbr-img-wrap { 
  position: relative; width: 100%; aspect-ratio: 3/4; overflow: hidden; 
}
.sbr-img-wrap video { 
  width: 100%; height: 100%; object-fit: cover; object-position: center top; 
  display: block; transition: transform 0.6s ease;
}
.sbr-card:hover .sbr-img-wrap video { transform: scale(1.06); }
.sbr-gradient { 
  position: absolute; inset: 0; z-index: 1;
  background: linear-gradient(
    to top,
    rgba(10,8,6,0.97) 0%,
    rgba(10,8,6,0.6) 35%,
    rgba(10,8,6,0.15) 65%,
    transparent 100%
  );
  transition: opacity 0.3s;
}
.sbr-card:hover .sbr-gradient { opacity: 0.85; }

/* gold shimmer line at top */
.sbr-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(to right, transparent, #B08D57, transparent);
  z-index: 5;
  opacity: 0;
  transition: opacity 0.3s;
}
.sbr-card:hover::before { opacity: 1; }

.sbr-top-row { 
  position: absolute; top: 0; left: 0; right: 0; z-index: 3; 
  padding: 14px 14px 0;
  display: flex; align-items: center; justify-content: space-between;
}
.sbr-handle-badge { 
  font-size: 9px; font-weight: 600; letter-spacing: 0.18em; 
  color: rgba(255,255,255,0.75);
  background: rgba(0,0,0,0.35);
  padding: 4px 10px;
  backdrop-filter: blur(4px);
}

/* reel icon badge */
.sbr-reel-dot {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: rgba(176,141,87,0.85);
  display: flex; align-items: center; justify-content: center;
}

.sbr-info { 
  position: absolute; bottom: 0; left: 0; right: 0; z-index: 3; 
  padding: 20px 16px 18px;
}
.sbr-card-title { 
  font-family: 'Playfair Display', serif; 
  font-size: 15px; font-weight: 700; 
  color: #fff; line-height: 1.3; 
  margin-bottom: 4px;
  text-shadow: 0 1px 8px rgba(0,0,0,0.5);
}
.sbr-card-sub {
  font-size: 10px; font-weight: 400; letter-spacing: 0.12em;
  color: rgba(255,255,255,0.55);
  margin-bottom: 14px;
  text-transform: uppercase;
}
.sbr-btns { display: flex; gap: 0; }
.sbr-btn-shop { 
  flex: 1; font-size: 9px; font-weight: 600; 
  letter-spacing: 0.2em; text-transform: uppercase; 
  background: #B08D57; color: #fff; 
  padding: 11px 8px; text-align: center;
  border: none; cursor: pointer;
  transition: background 0.22s, letter-spacing 0.22s;
  position: relative; overflow: hidden;
}
.sbr-btn-shop::after {
  content: '';
  position: absolute; inset: 0;
  background: rgba(255,255,255,0.08);
  transform: translateX(-100%);
  transition: transform 0.3s;
}
.sbr-card:hover .sbr-btn-shop::after { transform: translateX(0); }
.sbr-btn-shop:hover { background: #9A7A48; letter-spacing: 0.26em; }
        /* ornament */
        .sbr-ornament { display:flex; align-items:center; justify-content:center; gap:16px; margin-top:64px; opacity:0.35; }
        .sbr-ornament::before { content:''; width:80px; height:1px; background:linear-gradient(to right,transparent,#B08D57); }
        .sbr-ornament::after  { content:''; width:80px; height:1px; background:linear-gradient(to left,transparent,#B08D57); }
        .sbr-diamond { width:6px; height:6px; background:#B08D57; transform:rotate(45deg); }

        /* ═══════════════ MODAL ═══════════════ */
        .rm-backdrop { position:fixed; top:160px; left:0; right:0; bottom:0; z-index:10000; background:rgba(16,14,12,0.80); display:flex; align-items:center; justify-content:center; padding:16px 20px; backdrop-filter:blur(8px); }
        .rm-modal { position:relative; display:flex; width:100%; max-width:960px; height:100%; max-height:calc(100vh - 180px); background:#FAF8F4; box-shadow:0 48px 120px rgba(0,0,0,0.55); overflow:hidden; }
        .rm-close { position:absolute; top:12px; right:12px; z-index:20; width:32px; height:32px; border-radius:50%; background:rgba(28,26,24,0.9); border:none; color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.2s; }
        .rm-close:hover { background:#B08D57; }
        .rm-left { width:300px; flex-shrink:0; background:#1C1A18; display:flex; flex-direction:column; }
        .rm-video-area { flex:1; min-height:0; position:relative; overflow:hidden; }
        .rm-cover-fallback { width:100%; height:100%; position:relative; }
        .rm-cover-fallback img { width:100%; height:100%; object-fit:cover; object-position:center top; display:block; }
        .rm-cover-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(28,26,24,0.88) 0%,rgba(28,26,24,0.2) 60%,transparent 100%); display:flex; flex-direction:column; align-items:center; justify-content:flex-end; padding:28px 20px; gap:12px; }
        .rm-play-circle { width:56px; height:56px; border-radius:50%; background:rgba(176,141,87,0.9); display:flex; align-items:center; justify-content:center; box-shadow:0 6px 28px rgba(0,0,0,0.4); }
        .rm-ig-link { font-size:9px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:#D4AF78; text-decoration:none; border-bottom:1px solid rgba(212,175,120,0.3); padding-bottom:2px; transition:color 0.2s; }
        .rm-ig-link:hover { color:#fff; }
        .rm-reel-strip { padding:14px 16px 16px; border-top:1px solid rgba(176,141,87,0.15); flex-shrink:0; }
        .rm-strip-handle { font-size:9px; color:rgba(255,255,255,0.4); letter-spacing:0.1em; margin-bottom:6px; }
        .rm-strip-title { font-family:'Playfair Display',serif; font-size:13px; font-weight:600; color:#fff; line-height:1.3; }
        .rm-right { flex:1; display:flex; flex-direction:column; overflow:hidden; min-width:0; }
        .rm-right-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:20px 22px 16px; border-bottom:1px solid rgba(176,141,87,0.15); flex-shrink:0; background:#fff; }
        .rm-right-eyebrow { font-size:9px; font-weight:600; letter-spacing:0.22em; text-transform:uppercase; color:#B08D57; margin-bottom:4px; }
        .rm-right-heading { font-family:'Playfair Display',serif; font-size:20px; font-weight:700; color:#1C1A18; line-height:1.1; }
        .rm-right-heading em { font-style:italic; font-weight:400; color:#B08D57; }
        .rm-btn-outline { font-family:'Jost',sans-serif; font-size:9px; font-weight:600; letter-spacing:0.16em; text-transform:uppercase; background:none; border:1px solid #1C1A18; color:#1C1A18; padding:9px 18px; cursor:pointer; white-space:nowrap; flex-shrink:0; transition:all 0.2s; }
        .rm-btn-outline:hover { background:#1C1A18; color:#fff; }
        .rm-prod-list { flex:1; overflow-y:auto; padding:14px 22px; display:flex; flex-direction:column; gap:10px; scrollbar-width:thin; scrollbar-color:#e0d8cc transparent; }
        .rm-prod-list::-webkit-scrollbar { width:4px; }
        .rm-prod-list::-webkit-scrollbar-thumb { background:#e0d8cc; border-radius:2px; }
        .rm-no-products { font-size:13px; color:#9A9186; text-align:center; padding:40px 0; font-style:italic; }
        .rm-prod-row { display:flex; align-items:center; gap:14px; cursor:pointer; padding:10px 12px; background:#fff; border:1px solid #f0ebe2; transition:border-color 0.2s, box-shadow 0.2s; }
        .rm-prod-row:hover { border-color:rgba(176,141,87,0.4); box-shadow:0 4px 16px rgba(28,26,24,0.07); }
        .rm-prod-thumb { width:68px; height:68px; flex-shrink:0; overflow:hidden; position:relative; background:#f5f0ea; }
        .rm-prod-thumb img { width:100%; height:100%; object-fit:cover; display:block; transition:transform 0.3s; }
        .rm-prod-row:hover .rm-prod-thumb img { transform:scale(1.07); }
        .rm-prod-thumb-hover { position:absolute; inset:0; background:rgba(28,26,24,0.5); display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity 0.2s; }
        .rm-prod-thumb-hover span { font-size:8px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; color:#fff; }
        .rm-prod-row:hover .rm-prod-thumb-hover { opacity:1; }
        .rm-prod-info { flex:1; min-width:0; }
        .rm-prod-name { font-family:'Playfair Display',serif; font-size:13px; font-weight:600; color:#1C1A18; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:5px; }
        .rm-prod-prices { display:flex; align-items:center; gap:8px; }
        .rm-prod-price { font-size:13px; font-weight:700; color:#1C1A18; }
        .rm-prod-orig  { font-size:11px; color:#b0a99e; text-decoration:line-through; }
        .rm-prod-view { font-family:'Jost',sans-serif; font-size:8px; font-weight:600; letter-spacing:0.16em; text-transform:uppercase; background:#1C1A18; color:#fff; border:none; padding:8px 16px; cursor:pointer; flex-shrink:0; transition:background 0.2s; }
        .rm-prod-view:hover { background:#B08D57; }
        .rm-right-foot { padding:14px 22px; border-top:1px solid rgba(176,141,87,0.15); background:#fff; flex-shrink:0; }
        .rm-btn-full { width:100%; font-family:'Jost',sans-serif; font-size:10px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; background:#1C1A18; color:#fff; border:none; padding:14px; cursor:pointer; transition:background 0.22s; }
        .rm-btn-full:hover { background:#B08D57; }

        /* ─── Responsive ─── */
/* ADD */
@media (max-width:1024px) { .sbr-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width:768px) {
  .sbr-page { padding-top:105px; }
  .sbr-grid { grid-template-columns: repeat(2, 1fr); }
  .rm-backdrop { top:105px; padding:12px; }
  .rm-modal { flex-direction:column; max-height:calc(100vh - 120px); height:auto; }
  .rm-left { width:100%; height:260px; flex-direction:row; }
  .rm-video-area { flex:1; }
  .rm-reel-strip { display:none; }
}
@media (max-width:540px) {
  .sbr-grid { grid-template-columns: repeat(2, 1fr); }
  .rm-left { flex-direction:column; height:240px; }
}
      `}</style>

      <div className="sbr-page">
        {/* Hero */}
        <div className="sbr-hero" ref={heroRef}>
          <motion.p className="sbr-hero-eyebrow" initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }}>Aamira Jewellery</motion.p>
          <motion.h1 className="sbr-hero-title" initial={{ opacity: 0, y: 24 }} animate={heroView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.12 }}>
            Shop by <em>Reel</em>
          </motion.h1>
          <motion.p className="sbr-hero-desc" initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.24 }}>
            Discover our viral looks — watch, fall in love, and wear it.
          </motion.p>
          <motion.p className="sbr-hero-tagline" initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.36 }}>
            ✦ &nbsp; Click any reel to watch &amp; shop
          </motion.p>
        </div>

        <div className="sbr-inner">
          {loading ? (
            <div className="sbr-loading">Loading reels…</div>
          ) : error ? (
            <div className="sbr-error">{error}</div>
          ) : reels.length === 0 ? (
            <div className="sbr-empty">
              <div className="sbr-empty-title">No reels yet</div>
              <p>Check back soon.</p>
            </div>
          ) : (
            <div className="sbr-grid">
              {reels.map((reel, i) => (
                <ReelCard key={reel.id} reel={reel} index={i} onOpen={handleOpen} />
              ))}
            </div>
          )}

          <motion.div className="sbr-ornament" initial={{ opacity: 0 }} whileInView={{ opacity: 0.35 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <div className="sbr-diamond" />
          </motion.div>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {activeReel && (
          <ReelModal reel={activeReel} onClose={() => setActiveReel(null)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default ShopByReelPage;